import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase, isConfigured, storePendingMarketingConsent, logEventWithIP, checkRateLimits } from '../services/supabase';
import { Mail, Loader2, ArrowRight, CheckCircle2, AlertCircle, X, Check, Bot, Globe } from 'lucide-react';
import { AGENT_LANGUAGES } from '../services/agentGuide';
import ToursAuthShell from './ToursAuthShell';

export type LoginApp = 'photoclear' | 'staging' | 'sketchplan' | 'tours' | 'worlds';

interface LoginThemeConfig {
    title: string;
    logo: string;
    /** Subtitle under "Welcome Back" (sign-in mode). */
    loginSubtitle: string;
    /** Subtitle under "Create Account" (sign-up mode). */
    signupSubtitle: string;
    accentLink: string;
    accentLinkHover: string;
    accentButton: string;
    accentButtonHover: string;
    accentButtonShadow: string;
    accentFocusRing: string;
    accentFocusBorder: string;
    accentInputIcon: string;
    accentCheckbox: string;
    termsLink: string;
    termsLinkHover: string;
    showBlueprintGrid: boolean;
}

const LOGIN_THEMES: Record<LoginApp, LoginThemeConfig> = {
    tours: {
        title: '360 Tours',
        logo: '/360tours-studio-logo-transparent.png',
        loginSubtitle: 'Sign in to manage your 360° tours.',
        signupSubtitle: 'Start building your first 360° tour for free.',
        accentLink: 'text-orange-400',
        accentLinkHover: 'hover:text-orange-300',
        accentButton: 'bg-orange-600',
        accentButtonHover: 'hover:bg-orange-500',
        accentButtonShadow: 'hover:shadow-orange-900/20',
        accentFocusRing: 'focus:ring-orange-500/50',
        accentFocusBorder: 'focus:border-orange-500',
        accentInputIcon: 'group-focus-within:text-orange-500',
        accentCheckbox: 'checked:bg-orange-600 checked:border-orange-600',
        termsLink: 'text-purple-400',
        termsLinkHover: 'hover:text-purple-300',
        showBlueprintGrid: false,
    },
    photoclear: {
        title: 'PhotoClear',
        logo: '/Photoclear Logo HR Trans.png',
        loginSubtitle: 'Sign in to access your dashboard.',
        signupSubtitle: 'Sign up to get 10 free credits and start creating tours!',
        accentLink: 'text-violet-400',
        accentLinkHover: 'hover:text-violet-300',
        accentButton: 'bg-violet-600',
        accentButtonHover: 'hover:bg-violet-500',
        accentButtonShadow: 'hover:shadow-violet-900/20',
        accentFocusRing: 'focus:ring-violet-500/50',
        accentFocusBorder: 'focus:border-violet-500',
        accentInputIcon: 'group-focus-within:text-violet-500',
        accentCheckbox: 'checked:bg-violet-600 checked:border-violet-600',
        termsLink: 'text-violet-400',
        termsLinkHover: 'hover:text-violet-300',
        showBlueprintGrid: false,
    },
    staging: {
        title: 'Virtual Staging Studio',
        logo: '/360tours-studio-logo.png',
        loginSubtitle: 'Sign in to access your dashboard.',
        signupSubtitle: 'Sign up to get 10 free credits and start creating tours!',
        accentLink: 'text-[#A855F7]',
        accentLinkHover: 'hover:text-[#C084FC]',
        accentButton: 'bg-[#8A2BE2]',
        accentButtonHover: 'hover:bg-[#9B3CF3]',
        accentButtonShadow: 'hover:shadow-[#8A2BE2]/30',
        accentFocusRing: 'focus:ring-[#8A2BE2]/50',
        accentFocusBorder: 'focus:border-[#8A2BE2]',
        accentInputIcon: 'group-focus-within:text-[#8A2BE2]',
        accentCheckbox: 'checked:bg-[#8A2BE2] checked:border-[#8A2BE2]',
        termsLink: 'text-[#A855F7]',
        termsLinkHover: 'hover:text-[#C084FC]',
        showBlueprintGrid: false,
    },
    sketchplan: {
        title: 'Sketchplan',
        logo: '/360tours-studio-logo.png',
        loginSubtitle: 'Sign in to access your dashboard.',
        signupSubtitle: 'Sign up to get 10 free credits and start creating tours!',
        accentLink: 'text-[#40E0D0]',
        accentLinkHover: 'hover:text-[#5EEAD4]',
        accentButton: 'bg-[#40E0D0]',
        accentButtonHover: 'hover:bg-[#5EEAD4]',
        accentButtonShadow: 'hover:shadow-[#40E0D0]/30',
        accentFocusRing: 'focus:ring-[#40E0D0]/50',
        accentFocusBorder: 'focus:border-[#40E0D0]',
        accentInputIcon: 'group-focus-within:text-[#40E0D0]',
        accentCheckbox: 'checked:bg-[#40E0D0] checked:border-[#40E0D0]',
        termsLink: 'text-[#40E0D0]',
        termsLinkHover: 'hover:text-[#5EEAD4]',
        showBlueprintGrid: true,
    },
    worlds: {
        title: '360Worlds',
        logo: '/360worlds-logo.png',
        loginSubtitle: 'Sign in to continue exploring 360Worlds.',
        signupSubtitle: 'Create your free account to start exploring immersive worlds.',
        accentLink: 'text-cyan-400',
        accentLinkHover: 'hover:text-cyan-300',
        accentButton: 'bg-cyan-600',
        accentButtonHover: 'hover:bg-cyan-500',
        accentButtonShadow: 'hover:shadow-cyan-900/20',
        accentFocusRing: 'focus:ring-cyan-500/50',
        accentFocusBorder: 'focus:border-cyan-500',
        accentInputIcon: 'group-focus-within:text-cyan-500',
        accentCheckbox: 'checked:bg-cyan-600 checked:border-cyan-600',
        termsLink: 'text-cyan-400',
        termsLinkHover: 'hover:text-cyan-300',
        showBlueprintGrid: false,
    },
};

/** A "NEW" sticker shown in the corner of a benefit tile. */
type BenefitBadge = { label: string; icon: 'agent' | 'globe' };

/** 9 benefit tiles per app for the login page grid (bold title + 1–2 line copy) */
const LOGIN_BENEFITS: Record<LoginApp, { title: string; description: string; badge?: BenefitBadge }[]> = {
    photoclear: [
        { title: 'AI Auto-Enhance', description: 'Instantly balance exposure, contrast, and color for professional-grade real estate photos.' },
        { title: 'Cloud Processing', description: 'Batch process hundreds of high-res images in minutes, not hours.' },
        { title: 'Virtual Decluttering', description: 'Automatically remove unwanted personal items to create a clean, neutral space.' },
        { title: 'Exterior Fixer', description: 'Swap grey skies for blue and enhance lush greenery with one click.' },
        { title: 'Wide-Angle Correction', description: 'Fix lens distortion for accurate spatial representation.' },
        { title: 'HDR Merging', description: 'Seamlessly blend multiple exposures for perfectly lit interiors.' },
        { title: 'Noise Reduction', description: 'Remove grain from low-light shots for crisp, clear results.' },
        { title: 'Object Removal', description: 'Erase tripods, power lines, or stray items effortlessly.' },
        { title: 'Brand Styling', description: 'Save custom presets to ensure all your property media matches your unique brand look.' },
    ],
    staging: [
        { title: 'AI Furniture Placement', description: 'Automatically populate empty rooms with photorealistic décor tailored to the room type.' },
        { title: 'Style Versatility', description: 'Choose from modern, traditional, or industrial styles to suit any demographic.' },
        { title: 'Cost Efficiency', description: 'Save thousands compared to physical staging and furniture rentals.' },
        { title: 'Speed to Market', description: 'Get staged images in hours, allowing you to list properties ahead of schedule.' },
        { title: 'Pre-Construction Sales', description: 'Market unbuilt properties by visualizing finished interior designs.' },
        { title: 'Virtual Renovation', description: 'Show "before and after" potential for fixer-uppers without lifting a hammer.' },
        { title: 'Spatial Context', description: 'Maintain accurate scale so buyers understand room dimensions and layout.' },
        { title: 'High-Res Rendering', description: 'Export marketing-ready, photorealistic images for portals and social media.' },
        { title: 'Integrated Workflow', description: 'Move directly from processed photos to staging within a single suite.' },
    ],
    sketchplan: [
        { title: '2D Floor Plans', description: 'Generate clean, dimensioned layouts with precise area calculations.' },
        { title: '3D Visualizations', description: 'Convert simple tours into immersive 3D dollhouse views.' },
        { title: 'Tour Integration', description: 'Link your floor plans directly to 360 virtual tours for complete spatial context.' },
        { title: 'No Manual Measuring', description: 'Generate accurate plans directly from your 360 captures—no tape measure required.' },
        { title: 'Multi-Language Labels', description: 'Provide plans in English, French, or Spanish to reach global buyers.' },
        { title: 'Portal Ready', description: 'Export files in JPG, PNG, or PDF formats compatible with all major property portals.' },
        { title: 'Time-Saving Automation', description: 'Skip hand-drawn sketches and wait just 24–48 hours for pro-grade drafts.' },
        { title: 'MLS Optimization', description: 'Ensure listings meet platform standards with clear, professional structural drafts.' },
        { title: 'Interactive Hotspots', description: 'Overlay floor plans with interactive tags to enrich the viewing experience.' },
    ],
    worlds: [
        { title: 'Immersive 360° Worlds', description: 'Step inside history, space, nature, biology and more — explore real places in full 360°.' },
        { title: 'Narrated & Multilingual', description: 'Guided narration and interactive hotspots turn every world into a lesson you can walk through.' },
        { title: 'Works Everywhere', description: 'Desktop, mobile, tablet and Meta Quest — explore straight from a link, no installation.' },
    ],
    tours: [
        { title: 'AI Agent Guide', description: 'A lifelike AI presenter walks buyers through every room with voice narration and on-screen captions.', badge: { label: 'New · Agent Mode', icon: 'agent' } },
        { title: 'Multi-Language Narration', description: 'Narrate tours in 18 languages. Visitors switch language on the fly — perfect for international buyers.', badge: { label: 'New · Multi-Language', icon: 'globe' } },
        { title: 'Upload & Organize', description: 'Drag and drop 360° images. Organize scenes with reordering and naming.' },
        { title: 'Interactive Hotspots', description: 'Place navigation between rooms. Add info points, video links and URLs.' },
        { title: 'Share & Embed', description: 'Get a shareable link or responsive embed code. Mobile-friendly viewer included.' },
        { title: 'Custom Branding', description: 'Add your logo, colours and nadir patch. Multiple brand profiles for clients.' },
        { title: 'Password Protection', description: 'Restrict access with a password gate. Perfect for pre-launch viewings.' },
        { title: 'Tour Analytics', description: 'Track views, unique visitors, popular scenes and visitor paths.' },
        { title: 'Floor Plans & Maps', description: 'Upload interactive floor plans with scene markers. Google Maps integration.' },
        { title: 'Video Hotspots', description: 'Embed YouTube videos inside tours. Walkthroughs or neighbourhood highlights.' },
        { title: 'Mobile & VR Ready', description: 'Gyroscope on mobile. VR stereo mode for Cardboard and phone headsets.' },
    ],
};

/** Tile border/icon accent classes per app (Rainbow, Purple, Turquoise, Orange) */
function getTileAccentClasses(app: LoginApp): { border: string; icon: string; hoverGlow: string } {
    switch (app) {
        case 'photoclear':
            return {
                border: 'border-violet-400/40 hover:border-violet-400/70',
                icon: 'text-violet-400',
                hoverGlow: 'hover:shadow-violet-500/20',
            };
        case 'staging':
            return {
                border: 'border-[#8A2BE2]/40 hover:border-[#8A2BE2]/70',
                icon: 'text-[#8A2BE2]',
                hoverGlow: 'hover:shadow-[#8A2BE2]/25',
            };
        case 'sketchplan':
            return {
                border: 'border-[#40E0D0]/40 hover:border-[#40E0D0]/70',
                icon: 'text-[#40E0D0]',
                hoverGlow: 'hover:shadow-[#40E0D0]/25',
            };
        case 'tours':
        default:
            return {
                border: 'border-orange-400/40 hover:border-orange-400/70',
                icon: 'text-orange-400',
                hoverGlow: 'hover:shadow-orange-500/20',
            };
    }
}

export interface WorldsPurchaseSummary {
    planName: string;
    payableLabel: string;
    renewalCopy: string;
    checkoutCopy: string;
}

interface LoginProps {
    onClose?: () => void;
    initialMode?: 'login' | 'signup';
    app?: LoginApp;
    /** When set, the worlds host shows a purchase-intent sign-up flow. */
    purchaseSummary?: WorldsPurchaseSummary;
    /** Keeps /login and /signup in the address bar when the visitor switches mode. */
    onModeChange?: (mode: 'login' | 'signup') => void;
}

const GoogleMark = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
);

const Login: React.FC<LoginProps> = ({ onClose, initialMode = 'login', app = 'tours', purchaseSummary, onModeChange }) => {
    const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mouse, setMouse] = useState(() => ({
        x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
        y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
    }));
    const theme = LOGIN_THEMES[app];
    const purchaseFlow = Boolean(purchaseSummary);

    // Capture referral code from URL
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const refCode = params.get('ref');
      if (refCode) { localStorage.setItem('referral_code', refCode);
      try {
        document.cookie = `rehub_referral_code=${encodeURIComponent(refCode)}; path=/; max-age=${60 * 60 * 24 * 14}; SameSite=Lax`;
      } catch {} }
    }, []);

    const acceptedTermsState = useState(false);
    const [acceptedTerms, setAcceptedTerms] = acceptedTermsState;
    const acceptedMarketingState = useState(false);
    const [acceptedMarketing, setAcceptedMarketing] = acceptedMarketingState;
    const [website, setWebsite] = useState('');

    const handleGoogleSignIn = async () => {
        if (!acceptedTerms) {
            setError("Please accept the Terms and Conditions to continue.");
            return;
        }
        if (!isConfigured) {
            setError("System Error: Supabase connection not configured.");
            return;
        }
        setGoogleLoading(true);
        setError(null);
        try {
            if (mode === 'signup' && acceptedMarketing) {
                localStorage.setItem('pending_marketing_consent', 'true');
            }
            const callbackSearch = window.location.search || '';
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback${callbackSearch}`,
                    queryParams: { access_type: 'offline', prompt: 'consent' },
                },
            });
            if (error) throw error;
        } catch (err: any) {
            console.error('Google Sign-In Error:', err);
            setError(err.message || 'Failed to sign in with Google');
            setGoogleLoading(false);
        }
    };

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        setMouse({ x: e.clientX, y: e.clientY });
    }, []);

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!acceptedTerms) {
            setError('Please accept the Terms and Conditions to continue.');
            setLoading(false);
            return;
        }

        if (website.trim()) {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            return;
        }

        if (!isConfigured) {
            setError('System Error: Supabase connection not configured. Missing API Keys.');
            setLoading(false);
            return;
        }

        try {
            const rateLimitCheck = await checkRateLimits(email);
            if (!rateLimitCheck.allowed) {
                setError(rateLimitCheck.message || 'Too many attempts. Please try again later.');
                setLoading(false);
                return;
            }

            const callbackSearch = window.location.search || '';
            const options: any = {
                emailRedirectTo: `${window.location.origin}/auth/callback${callbackSearch}`,
                data: { signup_app: app === 'worlds' ? 'worlds' : 'tours' },
            };

            if (mode === 'signup' && acceptedMarketing) {
                await storePendingMarketingConsent(email);
            }
            if (mode === 'signup') {
                localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
            }

            const { error } = await supabase.auth.signInWithOtp({
                email,
                options,
            });

            if (error) throw error;

            await logEventWithIP(
                'magic_link_requested',
                null,
                email,
                { mode, accepted_marketing: acceptedMarketing, user_agent: navigator.userAgent, source_app: app }
            );

            setSent(true);
        } catch (err: any) {
            console.error('Supabase Auth Error:', err);

            if (err.message === 'Failed to fetch' || !navigator.onLine) {
                setError('Connection failed. Please check your internet or firewall settings.');
            } else if (err.status === 400) {
                setError('Invalid email address or request.');
            } else {
                setError(err.message || 'Failed to send login link');
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleMode = () => {
        const next = mode === 'login' ? 'signup' : 'login';
        setMode(next);
        setError(null);
        setSent(false);
        setAcceptedTerms(false);
        setAcceptedMarketing(false);
        setWebsite('');
        onModeChange?.(next);
    };

    const toursEntry = app === 'tours' && !onClose && !purchaseFlow;

    useEffect(() => {
        if (!toursEntry) return;
        setMode(initialMode);
    }, [toursEntry, initialMode]);

    const renderGlowBackground = () => {
        if (onClose) return null;

        const baseGlowClass = 'absolute rounded-full pointer-events-none mix-blend-screen';

        const transition = { type: 'spring' as const, damping: 25, stiffness: 150 };
        const positionProps = {
            initial: { left: mouse.x, top: mouse.y },
            animate: { left: mouse.x, top: mouse.y },
            transition,
            style: { position: 'fixed' as const, transform: 'translate(-50%, -50%)' },
        };

        if (app === 'photoclear') {
            return (
                <motion.div
                    className={`${baseGlowClass} w-[480px] h-[480px] blur-[100px] opacity-40`}
                    {...positionProps}
                    style={{
                        ...positionProps.style,
                        background: 'conic-gradient(from 0deg at 50% 50%, #ec4899, #f59e0b, #84cc16, #06b6d4, #8b5cf6, #ec4899)',
                    }}
                />
            );
        }

        if (app === 'staging') {
            return (
                <motion.div
                    className={`${baseGlowClass} w-[480px] h-[480px] blur-[120px] opacity-30`}
                    {...positionProps}
                    style={{
                        ...positionProps.style,
                        background: 'radial-gradient(circle, rgba(138,43,226,0.5) 0%, rgba(47,47,47,0.4) 50%, transparent 70%)',
                    }}
                />
            );
        }

        if (app === 'sketchplan') {
            return (
                <motion.div
                    className={`${baseGlowClass} w-[400px] h-[400px] blur-[80px] opacity-35`}
                    {...positionProps}
                    style={{
                        ...positionProps.style,
                        background: 'radial-gradient(circle, rgba(64,224,208,0.5) 0%, rgba(64,224,208,0.15) 50%, transparent 70%)',
                    }}
                />
            );
        }

        // tours: soft purple/indigo glow following cursor
        return (
            <motion.div
                className={`${baseGlowClass} w-[400px] h-[400px] blur-[100px] opacity-25`}
                {...positionProps}
                style={{
                    ...positionProps.style,
                    background: 'radial-gradient(circle, rgba(88,28,135,0.4) 0%, rgba(67,56,202,0.3) 50%, transparent 70%)',
                }}
            />
        );
    };

    const authDivider = (
        <div className={`relative ${purchaseFlow ? 'my-3' : 'my-6'}`}>
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700"></div></div>
            <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-slate-900 text-slate-500">{purchaseFlow ? 'or email' : 'or continue with'}</span>
            </div>
        </div>
    );

    const googleButton = (
        <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className={`w-full flex items-center justify-center gap-3 ${purchaseFlow ? 'py-3' : 'py-3.5'} px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            {googleLoading ? (
                <Loader2 size={20} className="animate-spin text-gray-600" />
            ) : (
                <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                </>
            )}
        </button>
    );

    const consentBlock = (
        <div className={`${purchaseFlow ? 'mt-3 space-y-2.5 p-2.5' : 'mb-6 space-y-3 p-3'} text-left bg-slate-800/50 rounded-xl border border-slate-800`}>
            <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex items-center pt-0.5">
                    <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className={`peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border ${theme.accentCheckbox} transition-all`}
                    />
                    <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                    I accept the <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className={`${theme.termsLink} hover:underline ${theme.termsLinkHover} font-medium`} onClick={(e) => e.stopPropagation()}>Terms and Conditions</a> and <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className={`${theme.termsLink} hover:underline ${theme.termsLinkHover} font-medium`} onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
                </span>
            </label>

            {mode === 'signup' && (
                <label className="flex items-start gap-3 cursor-pointer group animate-fade-in">
                    <div className="relative flex items-center pt-0.5">
                        <input
                            type="checkbox"
                            checked={acceptedMarketing}
                            onChange={(e) => setAcceptedMarketing(e.target.checked)}
                            className={`peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border ${theme.accentCheckbox} transition-all`}
                        />
                        <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
                    </div>
                    <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                        I agree to receive marketing, offers, and updates from ReHub Studio. <span className="text-slate-500 text-[10px]">(Optional)</span>
                    </span>
                </label>
            )}
        </div>
    );

    if (toursEntry) {
        const fieldClass =
            'block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-[#db2777] focus:outline-none focus:ring-2 focus:ring-[#db2777]/20';

        return (
            <ToursAuthShell>
                {sent ? (
                    <div className="text-center">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                            <CheckCircle2 size={28} />
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Check your email</h2>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">
                            We&apos;ve sent a sign-in link to <span className="font-medium text-slate-900">{email}</span>.
                            <br />
                            Click it to {mode === 'signup' ? 'create your account' : 'sign in'}.
                        </p>
                        <button
                            type="button"
                            onClick={() => setSent(false)}
                            className="mt-6 text-sm font-medium text-[#db2777] hover:text-[#be185d]"
                        >
                            Try a different email
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 className="text-[1.65rem] font-semibold tracking-tight text-slate-900">
                            {mode === 'signup' ? 'Create your account' : 'Welcome back'}
                        </h2>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">
                            {mode === 'signup'
                                ? 'Start building your first 360° tour for free.'
                                : 'Sign in to manage your 360° tours.'}
                        </p>

                        {error && (
                            <div className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleAuth} className="mt-6 space-y-4">
                            <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                                <label htmlFor="website">Website</label>
                                <input
                                    type="text"
                                    id="website"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                                    Email address
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className={fieldClass}
                                />
                                <p className="text-xs text-slate-500">We&apos;ll email you a secure sign-in link.</p>
                            </div>

                            <div className="space-y-2 rounded-lg border border-slate-200/80 bg-white px-3 py-2">
                                <label className="flex min-h-[36px] items-center gap-2.5 cursor-pointer">
                                    <span className="relative flex">
                                        <input
                                            type="checkbox"
                                            checked={acceptedTerms}
                                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                                            className="peer h-4 w-4 shrink-0 appearance-none rounded border border-slate-300 bg-white checked:border-[#db2777] checked:bg-[#db2777] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#db2777] focus-visible:ring-offset-2"
                                        />
                                        <Check className="pointer-events-none absolute left-0.5 top-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3.5} />
                                    </span>
                                    <span className="text-xs leading-snug text-slate-600">
                                        I accept the{' '}
                                        <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-[#db2777] underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none" onClick={(e) => e.stopPropagation()}>
                                            Terms and Conditions
                                        </a>{' '}
                                        and{' '}
                                        <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-[#db2777] underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none" onClick={(e) => e.stopPropagation()}>
                                            Privacy Policy
                                        </a>
                                        .
                                    </span>
                                </label>
                                {mode === 'signup' && (
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <span className="relative mt-0.5 flex">
                                            <input
                                                type="checkbox"
                                                checked={acceptedMarketing}
                                                onChange={(e) => setAcceptedMarketing(e.target.checked)}
                                                className="peer h-4 w-4 shrink-0 appearance-none rounded border border-slate-300 bg-white checked:border-[#db2777] checked:bg-[#db2777] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#db2777] focus-visible:ring-offset-2"
                                            />
                                            <Check className="pointer-events-none absolute left-0.5 top-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3.5} />
                                        </span>
                                        <span className="text-xs leading-relaxed text-slate-600">
                                            I agree to receive marketing, offers, and updates from ReHub Studio.{' '}
                                            <span className="text-slate-400">(Optional)</span>
                                        </span>
                                    </label>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || googleLoading}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#db2777] px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#be185d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#db2777] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {loading ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    mode === 'signup' ? 'Create free account' : 'Sign in'
                                )}
                            </button>
                        </form>

                        <div className="relative my-5">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                                <span className="bg-[#f7f6f4] px-3 text-slate-400">or</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleGoogleSignIn}
                            disabled={loading || googleLoading}
                            className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#db2777] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {googleLoading ? <Loader2 size={18} className="animate-spin text-slate-500" /> : (
                                <>
                                    <GoogleMark />
                                    Continue with Google
                                </>
                            )}
                        </button>

                        <div className="mt-6 text-center text-sm text-slate-600">
                            {mode === 'signup' ? (
                                <>
                                    <p className="text-slate-500">10 free credits included</p>
                                    <p className="text-slate-500">No credit card required</p>
                                    <p className="mt-4">
                                        Already have an account?{' '}
                                        <button type="button" onClick={toggleMode} className="font-semibold text-[#db2777] hover:text-[#be185d]">
                                            Sign in
                                        </button>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p>
                                        New to 360Tours?{' '}
                                        <button type="button" onClick={toggleMode} className="font-semibold text-[#db2777] hover:text-[#be185d] focus-visible:outline-none focus-visible:underline">
                                            Create a free account
                                        </button>
                                    </p>
                                    <p className="mt-2 text-xs font-medium text-slate-500">10 free credits · No credit card required</p>
                                </>
                            )}
                        </div>
                    </>
                )}
            </ToursAuthShell>
        );
    }

    if (sent) {
        return (
            <div className="flex flex-col items-center justify-center p-6 animate-fade-in w-full h-full">
                <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center shadow-2xl relative">
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                        >
                            <X size={24} />
                        </button>
                    )}
                    <div className="flex flex-col items-center gap-3 mb-6">
                        <img src={theme.logo} alt="" className="h-10 w-auto max-w-[180px] object-contain" />
                        <p className="text-sm font-medium text-slate-400">{theme.title}</p>
                    </div>
                    <div className="w-16 h-16 bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-100 mb-2">Check your email</h2>
                    <p className="text-slate-400 mb-6">
                        {purchaseSummary ? (
                            <>
                                We have sent a sign-in link to <span className="text-slate-200 font-medium">{email}</span>.
                                <br />Click it to {mode === 'signup' ? 'create your account' : 'log in'}.
                                <br />
                                <span className="mt-2 inline-block text-sm text-slate-400">Payment is completed at checkout.</span>
                            </>
                        ) : (
                            <>
                                We've sent a magic link to <span className="text-slate-200 font-medium">{email}</span>.
                                <br />Click it to {mode === 'signup' ? 'create your account' : 'log in'}.
                            </>
                        )}
                    </p>
                    <button
                        onClick={() => setSent(false)}
                        className={`${theme.accentLink} ${theme.accentLinkHover} text-sm font-medium`}
                    >
                        Try a different email
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`flex flex-col items-center justify-center animate-fade-in relative overflow-hidden w-full h-full ${
                purchaseFlow ? 'p-0 min-h-0' : 'p-6 min-h-[550px]'
            }`}
            onMouseMove={handleMouseMove}
        >
            {/* Fixed blueprint grid (sketchplan only) */}
            {!onClose && theme.showBlueprintGrid && (
                <div
                    className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.08] z-0"
                    style={{
                        backgroundImage: `
                          linear-gradient(rgba(64,224,208,0.6) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(64,224,208,0.6) 1px, transparent 1px)
                        `,
                        backgroundSize: '24px 24px',
                    }}
                />
            )}

            {/* Mouse-follow glow layer */}
            {!onClose && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                    {renderGlowBackground()}
                </div>
            )}

            <div className={`max-w-md w-full bg-slate-900/90 backdrop-blur-xl border border-slate-800 shadow-2xl z-10 relative ${
                purchaseFlow ? 'rounded-2xl p-5 sm:p-6' : 'rounded-3xl p-8'
            }`}>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 min-h-11 min-w-11 inline-flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                )}

                <div className={`text-center ${purchaseFlow ? 'mb-3' : 'mb-6'}`}>
                    {purchaseFlow ? (
                        <div className="mb-2 flex justify-center pr-8">
                            <img
                                src={theme.logo}
                                alt=""
                                className="h-7 w-auto max-w-[132px] object-contain object-center"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-center mb-4">
                                <img
                                    src={theme.logo}
                                    alt={theme.title}
                                    className="h-12 sm:h-14 w-auto max-w-[220px] object-contain object-center shadow-xl"
                                />
                            </div>
                            <h2 className="text-lg font-semibold text-slate-300 mb-3">{theme.title}</h2>
                        </>
                    )}
                    <h1 id="worlds-auth-title" className={`${purchaseFlow ? 'text-xl' : 'text-2xl'} font-bold text-slate-100 ${purchaseFlow ? '' : 'mb-2'}`}>
                        {purchaseSummary
                            ? (mode === 'signup' ? 'Create your account to unlock every world' : 'Sign in to continue')
                            : mode === 'signup' ? 'Create Account' : 'Welcome Back'}
                    </h1>
                    {purchaseSummary ? (
                        <div className="mt-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5 text-left">
                            <p className="text-sm font-semibold text-cyan-100">{purchaseSummary.planName}</p>
                            <p className="mt-0.5 text-base font-bold text-white">{purchaseSummary.payableLabel}</p>
                            <p className="mt-0.5 text-xs leading-snug text-slate-300">{purchaseSummary.renewalCopy}</p>
                            <p className="mt-1 text-xs text-slate-400">{purchaseSummary.checkoutCopy}</p>
                        </div>
                    ) : (
                        <p className="text-slate-400">
                            {mode === 'signup' ? theme.signupSubtitle : theme.loginSubtitle}
                        </p>
                    )}
                    {app === 'worlds' && !purchaseSummary && (
                        <div
                            className="mt-4 grid grid-cols-9 justify-items-center gap-x-1.5 gap-y-1.5"
                            aria-label={`${AGENT_LANGUAGES.length} languages`}
                        >
                            {AGENT_LANGUAGES.map((l) => (
                                <span
                                    key={l.code}
                                    title={l.label}
                                    className="text-[1.7rem] leading-none"
                                    role="img"
                                    aria-label={l.label}
                                >
                                    {l.flag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {purchaseFlow && error && (
                    <div className="mb-3 flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm animate-shake">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                {purchaseFlow && (
                    <>
                        {googleButton}
                        {authDivider}
                    </>
                )}

                {!purchaseFlow && consentBlock}

                {!purchaseFlow && error && (
                    <div className="mb-6 flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm animate-shake">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleAuth} className={purchaseFlow ? 'space-y-3' : 'space-y-4'}>
                    <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                            type="text"
                            id="website"
                            name="website"
                            tabIndex={-1}
                            autoComplete="off"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                            Email Address
                        </label>
                        <div className="relative group">
                            <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 ${theme.accentInputIcon} transition-colors`}>
                                <Mail size={18} />
                            </div>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className={`block w-full pl-10 pr-3 ${purchaseFlow ? 'py-2.5' : 'py-3'} bg-black border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 ${theme.accentFocusRing} ${theme.accentFocusBorder} transition-all`}
                            />
                        </div>
                    </div>

                    {purchaseFlow && (
                        <p className="text-xs leading-snug text-slate-400">
                            We will email you a sign-in link — no password needed.
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading || googleLoading}
                        className={`w-full flex items-center justify-center gap-2 ${purchaseFlow ? 'py-3' : 'py-3.5'} px-4 ${theme.accentButton} ${theme.accentButtonHover} text-white rounded-xl font-semibold transition-all shadow-lg ${theme.accentButtonShadow} disabled:opacity-50 disabled:cursor-not-allowed group`}
                    >
                        {loading ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <>
                                {purchaseSummary
                                    ? 'Email me a sign-in link'
                                    : mode === 'signup' ? 'Create Account' : 'Sign In'}
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </button>
                </form>

                {purchaseFlow && consentBlock}

                {!purchaseFlow && (
                    <>
                        {authDivider}
                        {googleButton}
                    </>
                )}

                <div className={`${purchaseFlow ? 'mt-3 pt-3' : 'mt-6 pt-6'} border-t border-slate-800 text-center`}>
                    <p className="text-slate-500 text-xs">
                        {mode === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
                        <button
                            type="button"
                            onClick={toggleMode}
                            className={`${theme.accentLink} ${theme.accentLinkHover} font-medium transition-colors hover:underline`}
                        >
                            {mode === 'signup' ? 'Sign in' : (purchaseSummary ? 'Create an account' : 'Sign up for free')}
                        </button>
                    </p>
                </div>
            </div>

            {/* 3x3 benefit tiles below the card (full-page only); glassmorphism, adaptive accents, hover scale/glow */}
            {!onClose && app !== 'tours' && (
                <div className="w-full max-w-4xl mx-auto mt-10 px-4 pb-10 z-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {LOGIN_BENEFITS[app].map((benefit, i) => {
                            const accent = getTileAccentClasses(app);
                            const isPhotoclear = app === 'photoclear';
                            return (
                                <motion.div
                                    key={benefit.title}
                                    className={`
                                        relative rounded-xl p-4 md:p-5 overflow-hidden
                                        bg-slate-900/60 backdrop-blur-md
                                        border ${accent.border}
                                        transition-all duration-200
                                        ${accent.hoverGlow} hover:shadow-lg
                                    `}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.03, duration: 0.25 }}
                                    whileHover={{ scale: 1.03 }}
                                >
                                    {isPhotoclear && (
                                        <div
                                            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                                            style={{ background: 'linear-gradient(180deg, #ec4899, #f59e0b, #84cc16, #06b6d4, #8b5cf6, #ec4899)' }}
                                        />
                                    )}
                                    {benefit.badge && (
                                        <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-full bg-orange-500/15 border border-orange-400/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300 shadow-sm">
                                            {benefit.badge.icon === 'agent' ? <Bot size={11} /> : <Globe size={11} />}
                                            {benefit.badge.label}
                                        </span>
                                    )}
                                    <div className={`${isPhotoclear ? 'pl-3' : ''} ${benefit.badge ? 'pt-6' : ''}`}>
                                        <h3 className={`font-bold text-slate-100 mb-1.5 text-sm md:text-base ${accent.icon}`}>
                                            {benefit.title}
                                        </h3>
                                        <p className="text-slate-400 text-xs md:text-sm leading-snug">
                                            {benefit.description}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
    );
};

export default Login;
